import { db } from "../../core/data/local/db";
import type {
  ModuleDefinition,
  ModuleSubscribers,
} from "../../core/platform/registry/registry";
import type {
  SaleFinalizedEvent,
  SaleVoidedEvent,
  SaleRefundedEvent,
  ShiftOpenedEvent,
} from "../../core/domain/events/bus";
import { composeTicket } from "../../core/platform/receipt/compose-ticket";
import { TicketQueue, type TicketPrintJob } from "./ticket-queue";
import { startOrderNumbering } from "./order-numbers";

/**
 * The ticket module (T009, T011, T012, T023, T024 — specs/002-barista-order-tickets).
 *
 * Mirrors drawer-module.ts's shape: it subscribes to core events; core never
 * calls it. Unlike the drawer's `saleShouldOpenDrawer`, ticket printing is
 * TENDER-BLIND — FR-201 prints for every finalized sale, cash or not, because
 * the barista needs the item list regardless of how the customer paid.
 *
 * NOT BLOCKING THE TILL (FR-211, R7). The subscriber returns once
 * `TicketQueue.enqueue()` resolves — durably recorded as `waiting` — not once
 * the ticket has actually printed. `enqueue()` itself kicks off the worker
 * without being awaited by its caller.
 */

export interface TicketHardware {
  isConfigured(): boolean;
}

export interface TicketDeps {
  hardware: TicketHardware;
  queue: TicketQueue;
  /** Resolved from db.staff — see composeTicket's cashierName input. */
  cashierName: (staffId: string) => Promise<string>;
  /** Assigned per specs/002-barista-order-tickets research.md R5 — see order-numbers.ts. */
  orderNumber: (shiftId: string) => Promise<string>;
}

/**
 * Compose and enqueue a ticket for a finalized sale.
 *
 * No printer ever paired on this terminal (`!hardware.isConfigured()`) means
 * nothing was expected to print — the identical rule `attemptRelease` already
 * applies to the drawer (FR-216). Reads `db.sales`/`db.saleLines` directly
 * rather than trusting the event payload, because `SaleFinalizedEvent` carries
 * ids, not the name snapshots a ticket needs — the same directionality
 * `recorder.ts` already uses reading `db.meta` (a module reading core's local
 * replica, never writing to it).
 */
export async function enqueueTicketForSale(
  deps: TicketDeps,
  event: { saleId: string; staffId: string; shiftId: string },
): Promise<TicketPrintJob | null> {
  if (!deps.hardware.isConfigured()) return null;

  const sale = await db.sales.get(event.saleId);
  if (!sale) return null;

  const lines = await db.saleLines.where("saleId").equals(event.saleId).toArray();
  if (lines.length === 0) return null;

  const [cashierName, orderNumber] = await Promise.all([
    deps.cashierName(event.staffId),
    deps.orderNumber(event.shiftId),
  ]);

  const document = composeTicket({ sale, lines, cashierName, orderNumber });
  return deps.queue.enqueue(event.saleId, document);
}

/**
 * Did this sale's ticket already print? Read from the queue's own in-memory
 * jobs — populated by `loadPending()` at enable time, so this is correct even
 * across a reload (T024, FR-228).
 */
function ticketAlreadyPrinted(deps: TicketDeps, saleId: string): TicketPrintJob | undefined {
  return deps.queue.jobs.find((job) => job.saleId === saleId && job.state === "printed");
}

export interface TicketPrompts {
  /** Asks the cashier to acknowledge a sale voided/refunded after printing. */
  requestVoidAcknowledgement(orderNumber: string): Promise<void>;
}

export function createTicketModule(
  deps: TicketDeps,
  prompts: TicketPrompts,
): Pick<ModuleDefinition, "subscribers"> {
  const subscribers: ModuleSubscribers = {
    "sale.finalized": async (event: SaleFinalizedEvent) => {
      await enqueueTicketForSale(deps, {
        saleId: event.saleId,
        staffId: event.staffId,
        shiftId: event.shiftId,
      });
    },

    "sale.voided": async (event: SaleVoidedEvent) => {
      const job = ticketAlreadyPrinted(deps, event.saleId);
      if (job) await prompts.requestVoidAcknowledgement(job.document.orderNumber);
    },

    "sale.refunded": async (event: SaleRefundedEvent) => {
      const job = ticketAlreadyPrinted(deps, event.saleId);
      if (job) await prompts.requestVoidAcknowledgement(job.document.orderNumber);
    },

    // Restarts order numbering for the new shift (T030, FR-224). A no-op if
    // this shift's series already exists — config arrives twice on every
    // boot, and a second shift.opened must not roll a count back mid-shift.
    "shift.opened": async (event: ShiftOpenedEvent) => {
      await startOrderNumbering(event.shiftId);
    },
  };

  return { subscribers };
}
